import type MockAdapter from "axios-mock-adapter"

const TEAM_MEMBER = {
  userId: "user-3",
  userName: "Thabo Mokoena",
  userEmail: "teammate@example.com",
}

interface MockAssignment {
  id: string
  resourceType: string
  resourceId: string
  userId: string
  userName: string
  userEmail: string
  level: "Read" | "Edit"
  assignedBy: string
  assignedAt: string
}

// job-2 (see mock-vacancy-service.ts) starts restricted to the mock TeamMember, so the "Manage
// access" panel and read-level UI have something to show without any manual setup.
const assignments: MockAssignment[] = [
  {
    id: "assign-1",
    resourceType: "Vacancy",
    resourceId: "job-2",
    userId: TEAM_MEMBER.userId,
    userName: TEAM_MEMBER.userName,
    userEmail: TEAM_MEMBER.userEmail,
    level: "Read",
    assignedBy: "user-1",
    assignedAt: new Date().toISOString(),
  },
]

function forResource(type: string, resourceId: string) {
  return assignments.filter((a) => a.resourceType === type && a.resourceId === resourceId)
}

export function registerResourceAccessMocks(mock: MockAdapter) {
  // ── Assignees for every restricted record of a type (list decoration) ───
  mock
    .onGet(new RegExp(`/organizations/[^/]+/resource-access/(Vacancy|Candidate|Employee)$`))
    .reply((config) => {
      const type = config.url?.match(/resource-access\/([^/?]+)/)?.[1]
      const rows = assignments
        .filter((a) => a.resourceType === type)
        .reduce<Record<string, MockAssignment[]>>((acc, a) => {
          acc[a.resourceId] = [...(acc[a.resourceId] ?? []), a]
          return acc
        }, {})
      return [
        200,
        Object.entries(rows).map(([resourceId, rowAssignments]) => ({
          resourceId,
          assignees: rowAssignments.map((a) => ({
            userId: a.userId,
            userName: a.userName,
            level: a.level,
          })),
        })),
      ]
    })

  // ── Caller's own level on one record ─────────────────────────────────────
  mock
    .onGet(new RegExp(`/organizations/[^/]+/resource-access/[^/]+/[^/]+/me$`))
    .reply((config) => {
      const parts = config.url?.split("/") ?? []
      const resourceId = parts[parts.length - 2]
      const type = parts[parts.length - 3]
      const rows = forResource(type ?? "", resourceId ?? "")
      return [
        200,
        {
          resourceType: type,
          resourceId,
          level: rows.length === 0 ? "Edit" : "Edit",
          isRestricted: rows.length > 0,
          isPrivileged: true,
        },
      ]
    })

  // ── Assignees of one record ───────────────────────────────────────────────
  mock
    .onGet(new RegExp(`/organizations/[^/]+/resource-access/[^/]+/[^/]+$`))
    .reply((config) => {
      const parts = config.url?.split("/") ?? []
      const resourceId = parts[parts.length - 1]
      const type = parts[parts.length - 2]
      return [200, forResource(type ?? "", resourceId ?? "")]
    })

  // ── Assign / change level ─────────────────────────────────────────────────
  mock
    .onPut(new RegExp(`/organizations/[^/]+/resource-access/[^/]+/[^/]+/assignees/[^/]+`))
    .reply((config) => {
      const parts = config.url?.split("/") ?? []
      const userId = parts[parts.length - 1]
      const resourceId = parts[parts.length - 3]
      const type = parts[parts.length - 4]
      const body = JSON.parse(config.data ?? "{}")

      const existing = assignments.find(
        (a) => a.resourceType === type && a.resourceId === resourceId && a.userId === userId,
      )

      const dto: MockAssignment = existing
        ? { ...existing, level: body.level }
        : {
            id: `assign-${Date.now()}`,
            resourceType: type ?? "",
            resourceId: resourceId ?? "",
            userId: userId ?? "",
            userName: userId === TEAM_MEMBER.userId ? TEAM_MEMBER.userName : "Mock User",
            userEmail: userId === TEAM_MEMBER.userId ? TEAM_MEMBER.userEmail : "mock@example.com",
            level: body.level,
            assignedBy: "user-1",
            assignedAt: new Date().toISOString(),
          }

      if (existing) {
        Object.assign(existing, dto)
      } else {
        assignments.push(dto)
      }

      return [200, dto]
    })

  // ── Revoke ─────────────────────────────────────────────────────────────
  mock
    .onDelete(new RegExp(`/organizations/[^/]+/resource-access/[^/]+/[^/]+/assignees/[^/]+`))
    .reply((config) => {
      const parts = config.url?.split("/") ?? []
      const userId = parts[parts.length - 1]
      const resourceId = parts[parts.length - 3]
      const type = parts[parts.length - 4]

      const index = assignments.findIndex(
        (a) => a.resourceType === type && a.resourceId === resourceId && a.userId === userId,
      )
      if (index >= 0) assignments.splice(index, 1)

      return [204]
    })

  // ── Audit log ──────────────────────────────────────────────────────────
  mock
    .onGet(new RegExp(`/organizations/[^/]+/resource-access/audit-log`))
    .reply(200, { items: [], totalCount: 0, page: 1, pageSize: 25 })
}
